import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Fade,
  Stack,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useLocation } from "react-router-dom";
import { WHATSAPP_DEFAULT_MESSAGE, WHATSAPP_PHONE } from "../config";

function buildWhatsAppUrl(phone: string, message: string) {
  const cleanPhone = (phone || "").replace(/[^\d]/g, "");
  const text = encodeURIComponent(message || "");
  return `https://wa.me/${cleanPhone}?text=${text}`;
}

const SESSION_KEY = "hs_whatsapp_widget_dismissed";

const WhatsAppWidget: React.FC = () => {
  const location = useLocation();

  const hideOnAdmin = useMemo(() => {
    return location.pathname.startsWith("/admin");
  }, [location.pathname]);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (hideOnAdmin) return;

    const dismissed = sessionStorage.getItem(SESSION_KEY) === "1";
    if (dismissed) return;

    const t = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(t);
  }, [hideOnAdmin]);

  if (hideOnAdmin) return null;

  const waUrl = buildWhatsAppUrl(WHATSAPP_PHONE, WHATSAPP_DEFAULT_MESSAGE);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  return (
    <Box
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 1,
        pointerEvents: "none",
      }}
    >
      {/* Popup card */}
      <Fade in={open}>
        <Paper
          elevation={6}
          sx={{
            width: 280,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.08)",
            pointerEvents: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              bgcolor: "#25D366",
              color: "#fff",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <WhatsAppIcon />
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                WhatsApp
              </Typography>
            </Stack>

            <IconButton
              size="small"
              onClick={close}
              sx={{ color: "#fff" }}
              aria-label="Close WhatsApp popup"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
              Need help?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Chat with us on WhatsApp for pricing, orders, and custom requests.
            </Typography>

            <Button
              component="a"
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              fullWidth
              variant="contained"
              startIcon={<WhatsAppIcon />}
              sx={{
                mt: 1.5,
                bgcolor: "#25D366",
                "&:hover": { bgcolor: "#1fb857" },
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Chat on WhatsApp
            </Button>
          </Box>
        </Paper>
      </Fade>

      {/* Floating button (always visible) */}
      <Tooltip title="Chat on WhatsApp" placement="left">
        <IconButton
          component="a"
          href={waUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => setOpen(false)}
          sx={{
            pointerEvents: "auto",
            width: 56,
            height: 56,
            bgcolor: "#25D366",
            color: "#fff",
            boxShadow: "0 10px 25px rgba(0,0,0,0.20)",
            "&:hover": { bgcolor: "#1fb857" },
          }}
          aria-label="Open WhatsApp chat"
        >
          <WhatsAppIcon />
        </IconButton>
      </Tooltip>

      {/* Small "open" link when closed */}
      {!open && (
        <Button
          onClick={() => setOpen(true)}
          sx={{
            pointerEvents: "auto",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 12,
            px: 1.25,
            py: 0.6,
            borderRadius: 999,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.12)",
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            "&:hover": { bgcolor: "#fafafa" },
          }}
        >
          Need help? Chat
        </Button>
      )}
    </Box>
  );
};

export default WhatsAppWidget;
